import { useState } from "react";
import { Card, Table, Tag, Progress, Avatar, Select, Button, Space } from "antd";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import type { ColumnsType } from "antd/es/table";
import { TrophyOutlined, StarOutlined, RiseOutlined, TeamOutlined } from "@ant-design/icons";
import type { DashboardMetrics } from "../types";

const { Option } = Select;

interface RecruiterPerformanceProps {
    data: DashboardMetrics;
}

interface RecruiterData {
    key: string;
    id: string;
    name: string;
    email: string;
    positionsHandled: number;
    talentsContacted: number;
    interviewsScheduled: number;
    talentsHired: number;
    performanceScore: number;
    efficiency: number;
}

export const RecruiterPerformance = ({ data }: RecruiterPerformanceProps) => {
    const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
    const [sortBy, setSortBy] = useState<'score' | 'hires' | 'efficiency'>('score');

    const recruiters = data?.process_efficiency?.recruiter_productivity || [];

    const recruiterData: RecruiterData[] = recruiters.map((recruiter, index) => ({
        key: index.toString(),
        id: recruiter.id,
        name: recruiter.name,
        email: recruiter.email,
        positionsHandled: recruiter.positions_handled,
        talentsContacted: recruiter.talents_contacted,
        interviewsScheduled: recruiter.interviews_scheduled,
        talentsHired: recruiter.talents_hired,
        performanceScore: calculatePerformanceScore(recruiter),
        efficiency: calculateEfficiencyScore(recruiter),
    })).sort((a, b) => {
        if (sortBy === 'score') return b.performanceScore - a.performanceScore;
        if (sortBy === 'hires') return b.talentsHired - a.talentsHired;
        return b.efficiency - a.efficiency;
    });

    const topPerformers = recruiterData.slice(0, 3);
    const performanceTrend = [
        { month: 'Jan', score: 65, target: 70 },
        { month: 'Feb', score: 70, target: 72 },
        { month: 'Mar', score: 72, target: 75 },
        { month: 'Apr', score: 75, target: 75 },
        { month: 'May', score: 78, target: 75 },
        { month: 'Jun', score: 80, target: 78 },
        { month: 'Jul', score: 82, target: 80 },
    ];

    const columns: ColumnsType<RecruiterData> = [
        {
            title: 'Rank',
            key: 'rank',
            render: (_, __, index) => (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === 0 ? 'bg-yellow-100 text-yellow-600' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                            index === 2 ? 'bg-orange-100 text-orange-600' :
                                'bg-blue-50 text-blue-600'
                    }`}>
                    {index + 1}
                </div>
            ),
        },
        {
            title: 'Recruiter',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex items-center gap-3">
                    <Avatar size="large" style={{ backgroundColor: '#1890ff' }}>
                        {text.charAt(0)}
                    </Avatar>
                    <div>
                        <div className="font-medium">{text}</div>
                        <div className="text-xs text-gray-500">{record.email}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Hires',
            dataIndex: 'talentsHired',
            key: 'talentsHired',
            sorter: (a, b) => a.talentsHired - b.talentsHired,
            render: (value) => <Tag color="green">{value}</Tag>,
        },
        {
            title: 'Interviews',
            dataIndex: 'interviewsScheduled',
            key: 'interviewsScheduled',
            sorter: (a, b) => a.interviewsScheduled - b.interviewsScheduled,
        },
        {
            title: 'Performance Score',
            dataIndex: 'performanceScore',
            key: 'performanceScore',
            sorter: (a, b) => a.performanceScore - b.performanceScore,
            render: (value) => (
                <Progress
                    percent={value}
                    size="small"
                    status={value >= 80 ? 'success' : value >= 60 ? 'normal' : 'exception'}
                />
            ),
        },
        {
            title: 'Efficiency',
            dataIndex: 'efficiency',
            key: 'efficiency',
            render: (value) => `${value}%`,
        },
    ];

    function calculatePerformanceScore(recruiter: any): number {
        let score = 0;
        if (recruiter.talents_hired > 0) score += 30;
        if (recruiter.interviews_scheduled > 5) score += 20;
        if (recruiter.positions_handled > 3) score += 20;
        if (recruiter.talents_contacted > 10) score += 10;
        if (recruiter.profile_views > 50) score += 10;
        return Math.min(score, 100);
    }

    function calculateEfficiencyScore(recruiter: any): number {
        if (recruiter.talents_contacted === 0) return 0;
        const efficiency = (recruiter.talents_hired / recruiter.talents_contacted) * 100;
        return Math.round(Math.min(efficiency, 100));
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <Select value={timeRange} onChange={setTimeRange} className="w-32">
                        <Option value="week">This Week</Option>
                        <Option value="month">This Month</Option>
                        <Option value="quarter">This Quarter</Option>
                    </Select>
                    <Select value={sortBy} onChange={setSortBy} className="w-40">
                        <Option value="score">Performance Score</Option>
                        <Option value="hires">Number of Hires</Option>
                        <Option value="efficiency">Efficiency</Option>
                    </Select>
                </div>
                <Button type="primary">Export Report</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="text-center">
                    <TrophyOutlined className="text-3xl text-yellow-500 mb-2" />
                    <div className="text-2xl font-bold">{topPerformers[0]?.name || 'N/A'}</div>
                    <div className="text-sm text-gray-600">Top Performer</div>
                    <div className="text-lg font-bold text-yellow-600 mt-2">
                        {topPerformers[0]?.performanceScore || 0}%
                    </div>
                </Card>

                <Card className="text-center">
                    <StarOutlined className="text-3xl text-blue-500 mb-2" />
                    <div className="text-2xl font-bold">
                        {recruiterData.reduce((sum, r) => sum + r.talentsHired, 0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Hires</div>
                    <div className="text-lg font-bold text-green-600 mt-2">+12%</div>
                </Card>

                <Card className="text-center">
                    <RiseOutlined className="text-3xl text-green-500 mb-2" />
                    <div className="text-2xl font-bold">
                        {Math.round(recruiterData.reduce((sum, r) => sum + r.performanceScore, 0) / recruiterData.length) || 0}%
                    </div>
                    <div className="text-sm text-gray-600">Avg Performance</div>
                    <div className="text-lg font-bold text-green-600 mt-2">+5%</div>
                </Card>

                <Card className="text-center">
                    <TeamOutlined className="text-3xl text-purple-500 mb-2" />
                    <div className="text-2xl font-bold">{recruiterData.length}</div>
                    <div className="text-sm text-gray-600">Active Recruiters</div>
                    <div className="text-lg font-bold text-blue-600 mt-2">
                        {recruiterData.filter(r => r.performanceScore >= 70).length} Achievers
                    </div>
                </Card>
            </div>

            <Card title="Team Performance">
                <Table
                    columns={columns}
                    dataSource={recruiterData}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: true }}
                />
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Performance Trend">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={performanceTrend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke="#8884d8"
                                strokeWidth={2}
                                name="Actual Score"
                            />
                            <Line
                                type="monotone"
                                dataKey="target"
                                stroke="#82ca9d"
                                strokeWidth={2}
                                name="Target"
                                strokeDasharray="5 5"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Performance Distribution">
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={recruiterData.slice(0, 6)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="performanceScore" fill="#8884d8" name="Score" />
                            <Bar dataKey="efficiency" fill="#82ca9d" name="Efficiency" />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        </div>
    );
};